// ── 2-4. Reading cache ─────────────────────────────────────────────
// The key is built from the same evidence the request carries and NOTHING
// ELSE. No coordinates, no resonance, no timestamp. That is the whole point:
// moving a fragment and reading again must return the reading you already
// have, because the model was never told the fragment moved. If a coordinate
// ever reaches this function, the instrument stops being able to claim two
// spaces.
//
// The key is over the WHOLE SET, and that is not a convenience. The seed probe
// measured `wet swimsuit === sunburn` as strong in a ten-fragment set and weak
// in a six-fragment one: fit is a property of the set a fragment was read in,
// not of the pair. So a reading may only be reused for the exact set it was
// made from. Keying per fragment, or reusing relations across sets, would
// serve a claim the model never made about those fragments together.
// (docs/seed-selection.md · spec §0.9)
//
// The language a reading is written in is part of what the reading IS, not a
// rendering of it. An English reading and a Korean reading of the same canvas
// are two readings, so the language belongs in the key. Leaving it out is what
// let one key hold two different-language results at different times.

export const CACHE_STORE = "ic_readings";
export const CACHE_LIMIT = 50;

export async function readingCacheKey(forReading, lens, language, protocolVersion, modelVersion) {
  const material = JSON.stringify({
    // Sorted by id so the key describes the SET, not the order the fragments
    // happen to be listed in. The caller already sorts; doing it here too means
    // a future caller cannot silently split one set into two cache entries.
    fragments: [...forReading]
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      .map(f => ({ id: f.id, text: f.text })),
    lens,
    language,
    protocolVersion,
    modelVersion,
  });
  const bytes = new TextEncoder().encode(material);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, "0")).join("");
}

export function cacheRead(key) {
  try {
    const store = JSON.parse(localStorage.getItem(CACHE_STORE) || "{}");
    return store[key] || null;
  } catch { return null; }
}

export function cacheWrite(key, entry) {
  try {
    const store = JSON.parse(localStorage.getItem(CACHE_STORE) || "{}");
    store[key] = entry;
    // oldest out first; the canonical research record is the export, not this.
    const keys = Object.keys(store);
    if (keys.length > CACHE_LIMIT) {
      keys.sort((a, b) => (store[a].ts || "") < (store[b].ts || "") ? -1 : 1)
        .slice(0, keys.length - CACHE_LIMIT)
        .forEach(k => delete store[k]);
    }
    localStorage.setItem(CACHE_STORE, JSON.stringify(store));
  } catch { /* a full quota must never break a reading */ }
}
